import React, {useState} from 'react';

import {Button, Icon, Text} from '@albert-io/atomic';
import SchoolSearchInputs from 'client/components/SchoolSearchModal/SchoolSearchInputs.react';

import OnboardingPageLayout from '../OnboardingPageLayout';
import CreateHomeschoolForm from './CreateHomeschoolForm';
import CreatedHomeschoolCard from './CreatedHomeschoolCard';
import SchoolTypeCard from './SchoolTypeCard';
import {buildSchoolData, isContinueDisabled} from './schoolAssociation.utils';

import type {SchoolData} from '../onboarding.types';
import type {CreatedHomeschool, HomeschoolAction, SchoolType} from './schoolAssociation.types';

interface SchoolAssociationStepProps {
  onContinue: (data: SchoolData) => void;
  isSubmitting?: boolean;
  /** Used to pre-fill the new-homeschool name as "[LastName] Homeschool". */
  lastName?: string;
}

export default function SchoolAssociationStep({
  onContinue,
  isSubmitting,
  lastName
}: SchoolAssociationStepProps) {
  const [selectedType, setSelectedType] = useState<SchoolType>(null);
  const [homeschoolAction, setHomeschoolAction] = useState<HomeschoolAction>(null);
  const [selectedSchool, setSelectedSchool] = useState(undefined);
  const [createdHomeschool, setCreatedHomeschool] = useState<CreatedHomeschool | null>(null);

  const handleTypeSelect = (type: SchoolType) => {
    setSelectedType(type);
    setHomeschoolAction(null);
    setSelectedSchool(undefined);
    setCreatedHomeschool(null);
  };

  const showSearch =
    selectedType === 'school' ||
    (selectedType === 'homeschool' && homeschoolAction === 'join-existing');

  const handleContinue = () => {
    const data = buildSchoolData(selectedType, homeschoolAction, selectedSchool, createdHomeschool);
    onContinue(data);
  };

  return (
    <OnboardingPageLayout>
      <Text className='text-xxl font-black'>School Association</Text>
      <div className='flex flex-col gap-x3'>
        <div className='flex flex-col gap-x1'>
          <Text>Select an option</Text>
          <div className='flex gap-x2'>
            <SchoolTypeCard
              icon={['fal', 'school']}
              label='School'
              selected={selectedType === 'school'}
              onClick={() => handleTypeSelect('school')}
            />
            <SchoolTypeCard
              icon={['fal', 'home']}
              label='Homeschool/Tutor'
              selected={selectedType === 'homeschool'}
              onClick={() => handleTypeSelect('homeschool')}
            />
          </div>
        </div>
        {selectedType === 'homeschool' && homeschoolAction === 'join-existing' && (
          <Button
            color='secondary'
            variant='faded'
            roundness='pill'
            className='gap-x1-quarter self-start'
            onClick={() => {
              setSelectedSchool(undefined);
              setHomeschoolAction('create-new');
            }}
          >
            <Icon icon='arrow-left' />
            <Text color='secondary'>Create School Instead</Text>
          </Button>
        )}

        {showSearch && (
          <div className='overflow-x-hidden rounded-x1-half border border-primary p-x2'>
            <SchoolSearchInputs
              selected={selectedSchool}
              setSelected={setSelectedSchool}
              onClear={() => setSelectedSchool(undefined)}
              shouldShowHelpText={false}
              onSubmit={() => {}}
              hasExistingRecord={() => false}
              hideFooter
              setMaxHeight
              schoolTypeFilter={selectedType === 'homeschool' ? 'Homeschool' : undefined}
              excludeSchoolTypeFilter={selectedType === 'school' ? 'Homeschool' : undefined}
            />
          </div>
        )}

        {selectedType === 'homeschool' && homeschoolAction === null && (
          <div className='flex flex-col gap-x1-half'>
            <Button
              color='secondary'
              variant='outlined'
              roundness='pill'
              className='w-full gap-x1-quarter'
              onClick={() => setHomeschoolAction('join-existing')}
            >
              <Icon icon='search' />
              <Text color='secondary'>Join Existing</Text>
            </Button>
            <Button
              color='secondary'
              variant='outlined'
              roundness='pill'
              className='w-full gap-x1-quarter'
              onClick={() => setHomeschoolAction('create-new')}
            >
              <Icon icon='plus' />
              <Text color='secondary'>Create New</Text>
            </Button>
          </div>
        )}

        {selectedType === 'homeschool' && homeschoolAction === 'create-new' && (
          <CreateHomeschoolForm
            onSwitchToSearch={() => setHomeschoolAction('join-existing')}
            onCreated={(school) => {
              setCreatedHomeschool(school);
              setHomeschoolAction('created');
            }}
            initialData={createdHomeschool}
            defaultName={lastName?.trim() ? `${lastName.trim()} Homeschool` : ''}
          />
        )}

        {selectedType === 'homeschool' && homeschoolAction === 'created' && createdHomeschool && (
          <CreatedHomeschoolCard
            school={createdHomeschool}
            onDelete={() => {
              setCreatedHomeschool(null);
              setHomeschoolAction(null);
            }}
            onEdit={() => setHomeschoolAction('create-new')}
          />
        )}
      </div>

      <div className='flex flex-col items-center gap-x2'>
        <Button
          className='w-full'
          color='primary'
          size='l'
          roundness='pill'
          disabled={isContinueDisabled(
            selectedType,
            homeschoolAction,
            selectedSchool,
            createdHomeschool,
            isSubmitting
          )}
          onClick={handleContinue}
        >
          <Text color='primary-inverse-static'>{isSubmitting ? 'Saving...' : 'Continue'}</Text>
        </Button>
        <Text className='text-center'>
          Not finding your school?{' '}
          <a className='content-link' href='mailto:support@albert.io'>
            Contact Support
          </a>
        </Text>
      </div>
    </OnboardingPageLayout>
  );
}
